/**
 * Boomfection Landing — Chat Module
 * Chat messages, turn notifications, reactions
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */
import { t } from '/js/i18n.js';

/**
 * Adds a chat message to the chat panel
 * @param {Object} msg - message object
 * @param {string} [msg.text] - message text
 * @param {boolean} [msg.isSystem] - system message flag
 * @param {boolean} [msg.isQuickChat] - quick chat flag
 * @param {string} [msg.emote] - emote key for quick chat
 * @param {string} [msg.playerName] - sender name
 * @param {string} [msg.playerColor] - sender color CSS
 * @param {string} [msg.id] - message ID
 * @param {Object} [socketRef] - { socket } reference for reaction events
 */
export function addChatMessage(msg, socketRef) {
    const container = document.getElementById('chat-messages');
    const div = document.createElement('div');
    div.className = 'chat-message animate-slide-up';
    const msgId = msg.id || Date.now().toString(36) + Math.random().toString(36).substring(2, 7);
    div.dataset.messageId = msgId;

    if (msg.isSystem) {
        div.classList.add('system');
        const textSpan = document.createElement('span');
        textSpan.className = 'text';
        textSpan.textContent = msg.text;
        div.appendChild(textSpan);
    } else if (msg.isQuickChat) {
        const emotes = {
            gg: t('emoteGg'),
            gl: t('emoteGl'),
            oops: t('emoteOops'),
            nice: t('emoteNice'),
            hurry: t('emoteHurry'),
            think: t('emoteThink'),
            wow: t('emoteWow'),
            sorry: t('emoteSorry'),
            haha: t('emoteHaha'),
            what: t('emoteWhat'),
            no: t('emoteNo'),
            yes: t('emoteYes')
        };
        const sender = document.createElement('span');
        sender.className = 'sender';
        sender.style.color = msg.playerColor;
        sender.textContent = `${msg.playerName}:`;
        const textSpan = document.createElement('span');
        textSpan.className = 'text';
        textSpan.textContent = ` ${emotes[msg.emote] || msg.emote}`;
        div.appendChild(sender);
        div.appendChild(textSpan);
    } else {
        const sender = document.createElement('span');
        sender.className = 'sender';
        sender.style.color = msg.playerColor;
        sender.textContent = `${msg.playerName}:`;
        const textSpan = document.createElement('span');
        textSpan.className = 'text';
        textSpan.textContent = ` ${msg.text}`;
        div.appendChild(sender);
        div.appendChild(textSpan);
    }

    if (!msg.isSystem) {
        const reactionsBar = document.createElement('div');
        reactionsBar.className = 'chat-reactions';
        ['\u{1F44D}', '\u{1F602}', '\u{1F62E}'].forEach((emoji) => {
            const btn = document.createElement('span');
            btn.className = 'reaction-btn';
            btn.dataset.emoji = emoji;
            btn.textContent = emoji;
            btn.addEventListener('click', () => {
                if (socketRef?.socket) socketRef.socket.emit('chat:reaction', { messageId: msgId, emoji });
            });
            reactionsBar.appendChild(btn);
        });
        div.appendChild(reactionsBar);
    }

    container.appendChild(div);
    container.scrollTop = container.scrollHeight;
    while (container.children.length > 50) container.removeChild(container.firstChild);
}

/** @private turn notification timeout handle */
let turnNotificationTimeout = null;

/**
 * Shows a turn change notification overlay
 * @param {string} playerName
 * @param {string} playerColor - CSS color
 * @param {boolean} isMyTurn
 */
export function showTurnNotification(playerName, playerColor, isMyTurn) {
    const notification = document.getElementById('turn-notification');
    const nameEl = notification.querySelector('.turn-notification-name');
    if (turnNotificationTimeout) {
        clearTimeout(turnNotificationTimeout);
        notification.classList.remove('visible');
    }
    nameEl.textContent = playerName;
    notification.style.setProperty('--turn-color', playerColor || '#ff8c32');
    notification.classList.toggle('my-turn', isMyTurn);
    notification.classList.remove('hidden');
    void notification.offsetWidth;
    notification.classList.add('visible');
    turnNotificationTimeout = setTimeout(() => {
        notification.classList.remove('visible');
        setTimeout(() => notification.classList.add('hidden'), 300);
    }, 1500);
}
